import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ApiService from '../services/apiService';
import { ExportConfiguration, ExportPushResult, ZgwSearchResult, SummaryRef } from '../types';
import { logger } from '../utils/logger';

interface ZgwExportFlowProps {
    fileId: string;
    summaryRef: SummaryRef;
    /** Alle ZGW-configuraties voor dit template (bv. meerdere gemeenten) */
    configs: ExportConfiguration[];
    summaryTitle: string;
    /** Terug naar het gewone deelmenu */
    onBack: () => void;
    /** Hele deel-dialoog sluiten */
    onClose: () => void;
}

// zoeken op zaaknummer → zaak kiezen (in de resultatenlijst) → bevestigen met
// documenttype + vertrouwelijkheid → link-call → resultaat. Alle stappen leven
// in dezelfde dialoog.
type FlowStep = 'search' | 'confirm' | 'linking' | 'success' | 'error';

// Spiegel van de backend-validatie: de ZGW-identificatie is maximaal 40 tekens.
const MAX_IDENTIFICATIE_LENGTH = 40;

// Vertrouwelijkheidsaanduidingen zijn VNG-enumwaarden ("beperkt_openbaar");
// alleen de underscores vervangen, verder letterlijk tonen.
const formatVertrouwelijkheid = (value: string): string => value.replace(/_/g, ' ');

const ZgwExportFlow: React.FC<ZgwExportFlowProps> = ({
    fileId,
    summaryRef,
    configs,
    summaryTitle,
    onBack,
    onClose,
}) => {
    const { t, i18n } = useTranslation();
    const apiService = useMemo(() => new ApiService(), []);

    const [step, setStep] = useState<FlowStep>('search');
    const [selectedConfigId, setSelectedConfigId] = useState<string>(configs[0]?.id ?? '');
    const [identificatie, setIdentificatie] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [searchError, setSearchError] = useState<string | null>(null);
    // Zaakomschrijvingen kunnen persoonsgegevens bevatten: uitsluitend in deze
    // component-state houden; niet persisten, niet loggen, niet doorgeven aan
    // analytics.
    const [results, setResults] = useState<ZgwSearchResult[]>([]);
    const [vertrouwelijkheidOpties, setVertrouwelijkheidOpties] = useState<string[]>([]);
    const [linkConfigId, setLinkConfigId] = useState<string>('');
    const [selectedResult, setSelectedResult] = useState<ZgwSearchResult | null>(null);
    const [selectedIotUuid, setSelectedIotUuid] = useState<string>('');
    const [selectedVertrouwelijkheid, setSelectedVertrouwelijkheid] = useState<string>('');
    const [linkResult, setLinkResult] = useState<ExportPushResult | null>(null);

    const trimmedIdentificatie = identificatie.trim();
    const identificatieEmpty = trimmedIdentificatie.length === 0;
    const identificatieTooLong = trimmedIdentificatie.length > MAX_IDENTIFICATIE_LENGTH;

    // Resultaten horen bij één configuratie (gemeente); bij wisselen opnieuw zoeken.
    const changeConfig = (configId: string) => {
        setSelectedConfigId(configId);
        setResults([]);
        setHasSearched(false);
        setSearchError(null);
    };

    const search = async () => {
        if (identificatieEmpty || identificatieTooLong || isSearching) return;
        setIsSearching(true);
        setSearchError(null);
        try {
            const response = await apiService.searchZgw(
                fileId,
                summaryRef,
                trimmedIdentificatie,
                // config_id alleen meesturen als er echt iets te kiezen valt;
                // bij één configuratie lost de backend hem zelf op.
                configs.length > 1 ? selectedConfigId : undefined
            );
            setResults(response.results);
            setVertrouwelijkheidOpties(response.vertrouwelijkheidaanduidingen);
            setSelectedVertrouwelijkheid(
                response.vertrouwelijkheidaanduidingDefault ??
                response.vertrouwelijkheidaanduidingen[0] ??
                ''
            );
            setLinkConfigId(response.configId);
            setHasSearched(true);
        } catch (error: any) {
            // Alleen de foutstatus loggen; response-data kan persoonsgegevens bevatten.
            logger.error('ZGW search failed with status:', error?.response?.status);
            const message = error?.response?.data?.error;
            setSearchError(message ? message.toString() : t('shareDialog.zgw.error'));
        } finally {
            setIsSearching(false);
        }
    };

    const chooseResult = (result: ZgwSearchResult) => {
        setSelectedResult(result);
        // Voorselectie: het door de backend voorgestelde documenttype, anders
        // het eerste uit de lijst (leeg blijft leeg → koppelen niet mogelijk).
        const voorgesteld = result.informatieobjecttypen.find((iot) => iot.voorgesteld);
        setSelectedIotUuid(voorgesteld?.uuid ?? result.informatieobjecttypen[0]?.uuid ?? '');
        setStep('confirm');
    };

    const link = async () => {
        if (!selectedResult || !selectedIotUuid) return;
        setStep('linking');
        try {
            // platform_object_id = zaak-uuid : documenttype-uuid : vertrouwelijkheid;
            // zonder derde deel geldt de default uit de configuratie.
            const parts = [selectedResult.id, selectedIotUuid];
            if (selectedVertrouwelijkheid) {
                parts.push(selectedVertrouwelijkheid);
            }
            const result = await apiService.linkZgw(
                fileId,
                summaryRef,
                linkConfigId || selectedConfigId,
                parts.join(':')
            );
            setLinkResult(result);
            setStep(result.success ? 'success' : 'error');
        } catch (error: any) {
            logger.error('ZGW link failed with status:', error?.response?.status);
            const message = error?.response?.data?.error;
            setLinkResult({
                success: false,
                message: message ? message.toString() : t('shareDialog.zgw.error'),
            });
            setStep('error');
        }
    };

    // "2026-07-01" letterlijk als lokale datum tonen, zonder tijdzone-verschuiving.
    const formatStartdatum = (value?: string): string => {
        if (!value) return '';
        const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
        if (!match) return value;
        const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
        return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString(i18n.language);
    };

    const inputClass =
        'w-full px-3 py-2 text-sm rounded-lg border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500';
    const primaryButtonClass =
        'px-4 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
    const secondaryButtonClass =
        'px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors';

    if (step === 'search') {
        return (
            <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900">
                    {t('shareDialog.zgw.searchTitle')}
                </h3>

                {configs.length > 1 && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('shareDialog.zgw.configLabel')}
                        </label>
                        <select
                            value={selectedConfigId}
                            onChange={(e) => changeConfig(e.target.value)}
                            disabled={isSearching}
                            className={inputClass}
                        >
                            {configs.map((config) => (
                                <option key={config.id} value={config.id}>{config.name}</option>
                            ))}
                        </select>
                    </div>
                )}

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('shareDialog.zgw.searchLabel')}
                    </label>
                    <input
                        type="text"
                        value={identificatie}
                        onChange={(e) => setIdentificatie(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                search();
                            }
                        }}
                        placeholder={t('shareDialog.zgw.searchPlaceholder')}
                        disabled={isSearching}
                        autoFocus
                        className={inputClass}
                    />
                </div>

                {identificatieTooLong && (
                    <p className="text-xs text-red-600">
                        {t('shareDialog.zgw.searchMaxChars', { max: MAX_IDENTIFICATIE_LENGTH })}
                    </p>
                )}

                <div className="flex justify-end space-x-2">
                    <button onClick={onBack} disabled={isSearching} className={secondaryButtonClass}>
                        {t('shareDialog.zgw.back')}
                    </button>
                    <button
                        onClick={search}
                        disabled={identificatieEmpty || identificatieTooLong || isSearching}
                        className={primaryButtonClass}
                    >
                        {t('shareDialog.zgw.search')}
                    </button>
                </div>

                {isSearching && (
                    <div className="flex justify-center py-4">
                        <div className="w-5 h-5 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin"></div>
                    </div>
                )}

                {searchError && !isSearching && (
                    <div className="text-center space-y-2">
                        <p className="text-sm text-red-600">{searchError}</p>
                        <button onClick={search} className={secondaryButtonClass}>
                            {t('shareDialog.zgw.retry')}
                        </button>
                    </div>
                )}

                {hasSearched && !isSearching && !searchError && results.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-2">
                        {t('shareDialog.zgw.empty')}
                    </p>
                )}

                {hasSearched && !isSearching && !searchError && results.length > 0 && (
                    <div>
                        <div className="text-sm font-medium text-gray-700 mb-2">
                            {t('shareDialog.zgw.resultsLabel')}
                        </div>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                            {results.map((result) => (
                                <button
                                    key={result.id}
                                    onClick={() => chooseResult(result)}
                                    className="w-full flex items-center p-3 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors text-left"
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-gray-900 text-sm truncate">
                                            {result.identificatie}
                                        </div>
                                        {result.omschrijving && (
                                            <div className="text-gray-700 text-xs truncate">
                                                {result.omschrijving}
                                            </div>
                                        )}
                                        {(result.startdatum || result.zaaktypeOmschrijving) && (
                                            <div className="text-gray-500 text-xs truncate">
                                                {[formatStartdatum(result.startdatum), result.zaaktypeOmschrijving]
                                                    .filter(Boolean)
                                                    .join(' · ')}
                                            </div>
                                        )}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    if (step === 'confirm' && selectedResult) {
        const geenDocumenttypen = selectedResult.informatieobjecttypen.length === 0;
        return (
            <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900">
                    {t('shareDialog.zgw.searchTitle')}
                </h3>
                {/* Expliciete bevestiging: er wordt naar een gemeentelijk
                    zaakdossier geschreven. */}
                <p className="text-sm text-gray-900">
                    {t('shareDialog.zgw.confirm', {
                        title: summaryTitle,
                        identificatie: selectedResult.identificatie,
                    })}
                </p>
                <p className="text-xs text-gray-500">
                    {t('shareDialog.zgw.confirmWarning')}
                </p>
                {(selectedResult.omschrijving || selectedResult.startdatum || selectedResult.zaaktypeOmschrijving) && (
                    <div className="text-xs text-gray-500">
                        {[
                            selectedResult.omschrijving,
                            formatStartdatum(selectedResult.startdatum),
                            selectedResult.zaaktypeOmschrijving,
                        ]
                            .filter(Boolean)
                            .join(' · ')}
                    </div>
                )}

                {geenDocumenttypen ? (
                    // Zonder toegestane documenttypen kan er niet gekoppeld
                    // worden; dat vergt een beheerdersactie in het zaaksysteem.
                    <div className="px-3 py-2 text-xs rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
                        {t('shareDialog.zgw.noDocumentTypes')}
                    </div>
                ) : (
                    <>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('shareDialog.zgw.documentTypeLabel')}
                            </label>
                            <select
                                value={selectedIotUuid}
                                onChange={(e) => setSelectedIotUuid(e.target.value)}
                                className={inputClass}
                            >
                                {selectedResult.informatieobjecttypen.map((iot) => (
                                    <option key={iot.uuid} value={iot.uuid}>{iot.omschrijving}</option>
                                ))}
                            </select>
                        </div>
                        {vertrouwelijkheidOpties.length > 0 && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('shareDialog.zgw.confidentialityLabel')}
                                </label>
                                <select
                                    value={selectedVertrouwelijkheid}
                                    onChange={(e) => setSelectedVertrouwelijkheid(e.target.value)}
                                    className={inputClass}
                                >
                                    {vertrouwelijkheidOpties.map((value) => (
                                        <option key={value} value={value}>
                                            {formatVertrouwelijkheid(value)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </>
                )}

                <div className="flex justify-end space-x-2">
                    <button onClick={() => setStep('search')} className={secondaryButtonClass}>
                        {t('shareDialog.zgw.back')}
                    </button>
                    {!geenDocumenttypen && (
                        <button onClick={link} disabled={!selectedIotUuid} className={primaryButtonClass}>
                            {t('shareDialog.zgw.confirmAction')}
                        </button>
                    )}
                </div>
            </div>
        );
    }

    if (step === 'linking') {
        return (
            <div className="flex flex-col items-center py-8 text-center">
                <div className="w-8 h-8 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin mb-4"></div>
                <div className="text-sm font-medium text-gray-900">{t('shareDialog.zgw.busy')}</div>
                <div className="text-xs text-gray-500 mt-1">{t('shareDialog.zgw.busyHint')}</div>
            </div>
        );
    }

    if (step === 'success') {
        return (
            <div className="flex flex-col items-center py-6 text-center">
                <svg className="w-8 h-8 text-primary-600 mb-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-gray-900">
                    {linkResult?.message || t('shareDialog.zgw.success')}
                </p>
                <button onClick={onClose} className={`${secondaryButtonClass} mt-4`}>
                    {t('shareDialog.zgw.close')}
                </button>
            </div>
        );
    }

    // step === 'error' (of confirm zonder selectie, wat niet voor hoort te komen)
    return (
        <div className="flex flex-col items-center py-6 text-center">
            <svg className="w-8 h-8 text-red-600 mb-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {/* Backend-fouten integraal tonen: bij een partial failure noemt de
                melding het documentkenmerk en het advies om handmatig te
                koppelen — opnieuw proberen maakt dan een nieuw document aan. */}
            <p className="text-sm text-gray-900">
                {linkResult?.message || t('shareDialog.zgw.error')}
            </p>
            <div className="flex space-x-2 mt-4">
                {/* Geen automatische retries: opnieuw proberen is een bewuste
                    actie, anders kan het document dubbel landen. */}
                <button onClick={link} className={primaryButtonClass}>
                    {t('shareDialog.zgw.retry')}
                </button>
                <button onClick={() => setStep('search')} className={secondaryButtonClass}>
                    {t('shareDialog.zgw.back')}
                </button>
            </div>
        </div>
    );
};

export default ZgwExportFlow;
